/**
 * Transaction Detail Panel — slide-out panel with inline editing.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import { titleCaseCompanyName } from "@cvx/lib/companyName";
import { X, FileText, ChevronRight, Pencil, Check, Trash2 } from "lucide-react";
import { cn } from "@/utils/misc";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Textarea } from "@/ui/primitives/textarea";
import { Badge } from "@/ui/primitives/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import type { Id } from "@cvx/_generated/dataModel";
import {
  ChangeHistory,
  type ResolvedHistoryUser,
} from "../shared-panels/ChangeHistory";
import {
  ConfirmEditDialog,
  type PendingEdit,
} from "../shared-panels/ConfirmEditDialog";
import {
  ASSET_TRANSACTION_FIELD_FORMATTERS,
  ASSET_TRANSACTION_FIELD_LABELS,
} from "./historyFormatters";
import type { AssetTransactionUI } from "./types";
import { DocumentDetail } from "../../documents/DocumentDetail";

/**
 * `updateTransactionField` still declares `value: v.any()` since the
 * mutation accepts whatever type the target transaction field holds. Every
 * call site in this panel only ever saves a text/date string, a numeric
 * amount, or `undefined` (to clear a field) — that's what's typed here.
 */
type FieldSaveValue = string | number | undefined;

const fmtCurrency = (n?: number) =>
  n == null
    ? "\u2014"
    : n.toLocaleString("en-AU", {
        style: "currency",
        currency: "AUD",
        minimumFractionDigits: 2,
      });
const fmtDate = (d?: string) => {
  if (!d) return "\u2014";
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
};

const DIRECTION_LABELS: Record<string, string> = {
  Sale: "Sale",
  Purchase: "Purchase",
};
const CATEGORY_LABELS: Record<string, string> = {
  "Asset Disposal": "Asset Disposal",
  "Asset Acquisition": "Asset Acquisition",
  "Stock Sale": "Stock Sale",
  "Stock Purchase": "Stock Purchase",
  "Debt Assignment": "Debt Assignment",
  "Lease Assignment": "Lease Assignment",
  "IP Transfer": "IP Transfer",
  Goodwill: "Goodwill",
  Other: "Other",
};
const CLASS_LABELS: Record<string, string> = {
  Property: "Property",
  "Plant & Equipment": "Plant & Equipment",
  "Motor Vehicles": "Motor Vehicles",
  Inventory: "Inventory",
  Receivables: "Receivables",
  "Intellectual Property": "Intellectual Property",
  "Shares/Securities": "Shares/Securities",
  Goodwill: "Goodwill",
  "Other Intangibles": "Other Intangibles",
  Other: "Other",
};
const PAYMENT_LABELS: Record<string, string> = {
  Paid: "Paid",
  "Partially Paid": "Partially Paid",
  Unpaid: "Unpaid",
  Overdue: "Overdue",
  "Written Off": "Written Off",
  Unknown: "Unknown",
};
const AGREEMENT_LABELS: Record<string, string> = {
  Executed: "Executed",
  Draft: "Draft",
  Unsigned: "Unsigned",
  Partial: "Partial",
  "Not Found": "Not Found",
};
const REVIEW_LABELS: Record<string, string> = {
  Pending: "Pending",
  "In Review": "In Review",
  Reviewed: "Reviewed",
  Escalated: "Escalated",
  Excluded: "Excluded",
};

function EditableField({
  label,
  value,
  displayValue,
  field,
  type = "text",
  onSave,
  isViewer,
}: {
  label: string;
  value?: string | number | null;
  displayValue?: string;
  field: string;
  type?: "text" | "date" | "number";
  onSave: (f: string, v: FieldSaveValue) => void;
  isViewer?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ""));
  const handleSave = () => {
    const p: FieldSaveValue =
      type === "number"
        ? draft
          ? parseFloat(draft)
          : undefined
        : draft || undefined;
    onSave(field, p);
    setEditing(false);
  };
  if (editing)
    return (
      <div className="flex items-center justify-between gap-2 py-2">
        <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
        <div className="flex items-center gap-1">
          <Input
            type={type}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="h-7 w-36 text-sm"
            onKeyDown={(e) => e.key === "Enter" && handleSave()}
            autoFocus
          />
          <button
            onClick={handleSave}
            className="rounded p-1 text-emerald-600 hover:bg-emerald-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => setEditing(false)}
            className="rounded p-1 text-muted-foreground hover:bg-muted"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  return (
    <div className="group flex items-center justify-between gap-4 py-2">
      <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
      <div className="flex items-center gap-1.5">
        <span className="text-right text-sm text-foreground">
          {displayValue || String(value ?? "\u2014")}
        </span>
        {!isViewer && (
          <button
            onClick={() => {
              setDraft(String(value ?? ""));
              setEditing(true);
            }}
            className="rounded p-0.5 text-muted-foreground/0 transition-colors hover:bg-muted group-hover:text-muted-foreground"
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}

function SelectField({
  label,
  value,
  options,
  field,
  onSave,
  isViewer,
}: {
  label: string;
  value: string;
  options: Record<string, string>;
  field: string;
  onSave: (f: string, v: FieldSaveValue) => void;
  isViewer?: boolean;
}) {
  if (isViewer)
    return (
      <div className="flex items-center justify-between gap-4 py-2">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className="text-sm text-foreground">
          {options[value] ?? value}
        </span>
      </div>
    );
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <Select value={value} onValueChange={(v) => onSave(field, v)}>
        <SelectTrigger className="h-7 w-44 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(options).map(([k, v]) => (
            <SelectItem key={k} value={k}>
              {v}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function EditableBannerField({
  label,
  value,
  field,
  onSave,
  isViewer,
}: {
  label: string;
  value: number;
  field: string;
  onSave: (f: string, v: FieldSaveValue) => void;
  isViewer?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  const handleSave = () => {
    onSave(field, draft ? parseFloat(draft) : 0);
    setEditing(false);
  };
  if (editing)
    return (
      <div>
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          {label}
        </div>
        <div className="flex items-center gap-1">
          <Input
            type="number"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="h-8 w-full font-mono text-lg font-bold"
            onKeyDown={(e) => e.key === "Enter" && handleSave()}
            autoFocus
          />
          <button onClick={handleSave} className="p-1 text-emerald-600">
            <Check className="h-4 w-4" />
          </button>
          <button
            onClick={() => setEditing(false)}
            className="p-1 text-muted-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  return (
    <div
      className={cn("group", !isViewer && "cursor-pointer")}
      onClick={() => {
        if (!isViewer) {
          setDraft(String(value));
          setEditing(true);
        }
      }}
    >
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
      <div className="flex items-center gap-1.5">
        <span className="font-mono text-xl font-bold tabular-nums">
          {fmtCurrency(value)}
        </span>
        {!isViewer && (
          <Pencil className="h-3 w-3 text-muted-foreground/0 transition-colors group-hover:text-muted-foreground" />
        )}
      </div>
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="pb-1.5 pt-6 text-xs font-medium uppercase tracking-widest text-[#121212]/70">
      {children}
    </div>
  );
}

interface TransactionDetailPanelProps {
  entry: AssetTransactionUI | null;
  open: boolean;
  onClose: () => void;
  onDeleted?: () => void;
  isViewer?: boolean;
}

export function TransactionDetailPanel({
  entry,
  open,
  onClose,
  onDeleted,
  isViewer,
}: TransactionDetailPanelProps) {
  const [visible, setVisible] = useState(false);
  const [viewingDocumentId, setViewingDocumentId] =
    useState<Id<"documents"> | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  // Edit awaiting practitioner confirmation (all inline edits pass through
  // the shared ConfirmEditDialog before any mutation fires).
  const [pendingEdit, setPendingEdit] = useState<PendingEdit | null>(null);

  const updateField = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.assetSalesPurchases.publicMutations
        .updateTransactionField,
    ),
  });
  const deleteTransaction = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.assetSalesPurchases.publicMutations
        .deleteTransaction,
    ),
  });

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => setVisible(true));
      setConfirmDelete(false);
    } else setVisible(false);
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  const historyUserIds = useMemo<Array<Id<"users">>>(() => {
    if (!entry?.changeHistory) return [];
    const ids = new Set<Id<"users">>();
    for (const change of entry.changeHistory) {
      if (change.userId) ids.add(change.userId);
    }
    return Array.from(ids);
  }, [entry?.changeHistory]);

  const { data: historyUsers } = useQuery({
    ...convexQuery(api.users.queries.listUsersByIds, {
      ids: historyUserIds,
    }),
    enabled: historyUserIds.length > 0,
  });

  const historyUsersById = useMemo(() => {
    const map = new Map<Id<"users">, ResolvedHistoryUser>();
    for (const u of historyUsers ?? []) {
      map.set(u._id, u);
    }
    return map;
  }, [historyUsers]);

  if (!entry) return null;

  // Stages the edit behind the shared confirmation dialog — the mutation only
  // fires once the practitioner confirms (optionally leaving a note).
  const handleFieldSave = (field: string, value: FieldSaveValue) => {
    setPendingEdit({
      fieldLabel: ASSET_TRANSACTION_FIELD_LABELS[field] ?? field,
      onConfirm: (note) =>
        updateField.mutateAsync({
          entryId: entry._id as Id<"assetTransactionEntries">,
          field,
          value,
          note,
        }),
    });
  };
  const handleDelete = () => {
    deleteTransaction.mutate(
      { entryId: entry._id as Id<"assetTransactionEntries"> },
      {
        onSuccess: () => {
          onDeleted?.();
          onClose();
        },
      },
    );
  };

  const dirConfig = {
    Sale: { color: "red" as const, label: "Sale" },
    Purchase: { color: "emerald" as const, label: "Purchase" },
  };
  const dir = dirConfig[entry.direction] ?? dirConfig.Sale;

  return (
    <>
      <div
        className={cn(
          "fixed inset-0 z-40 transition-opacity duration-200",
          visible
            ? "bg-black/18 dark:bg-black/40"
            : "pointer-events-none bg-transparent",
        )}
        onClick={onClose}
      />
      <div
        className={cn(
          "fixed right-0 top-0 z-50 h-full w-full max-w-lg overflow-y-auto border-l bg-background shadow-xl transition-transform duration-200 ease-out",
          visible ? "translate-x-0" : "translate-x-full",
        )}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 border-b bg-background/95 px-5 pb-3 pt-5 backdrop-blur-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              {!isViewer && editingName ? (
                <div className="flex items-center gap-1.5">
                  <Input
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    className="h-8 text-lg font-semibold"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleFieldSave("counterpartyName", nameDraft);
                        setEditingName(false);
                      }
                      if (e.key === "Escape") setEditingName(false);
                    }}
                    autoFocus
                  />
                  <button
                    onClick={() => {
                      handleFieldSave("counterpartyName", nameDraft);
                      setEditingName(false);
                    }}
                    className="p-1 text-emerald-600"
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setEditingName(false)}
                    className="p-1 text-muted-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div
                  className={cn("group", !isViewer && "cursor-pointer")}
                  onClick={() => {
                    if (!isViewer) {
                      setNameDraft(entry.counterpartyName);
                      setEditingName(true);
                    }
                  }}
                >
                  <div className="flex items-center gap-1.5">
                    <h2 className="mt-0.5 text-lg font-semibold leading-snug text-foreground">
                      {titleCaseCompanyName(entry.counterpartyName)}
                    </h2>
                    {!isViewer && (
                      <Pencil className="mt-1 h-3.5 w-3.5 text-muted-foreground/0 transition-colors group-hover:text-muted-foreground" />
                    )}
                  </div>
                </div>
              )}
              {entry.assetDescription && (
                <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                  {entry.assetDescription}
                </div>
              )}
            </div>
            <div className="mt-0.5 flex shrink-0 items-center gap-1">
              {!isViewer && (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30"
                  title="Delete"
                >
                  <Trash2 className="h-[16px] w-[16px]" />
                </button>
              )}
              <button
                onClick={onClose}
                className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="h-[18px] w-[18px]" />
              </button>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Badge color={dir.color} variant="soft" size="sm">
              {dir.label}
            </Badge>
            {entry.riskFlags.map((f) => (
              <Badge key={f} color="red" variant="soft" size="sm">
                {f}
              </Badge>
            ))}
            {entry.isManualOverride && (
              <Badge color="emerald" variant="soft" size="sm">
                Manually edited
              </Badge>
            )}
          </div>
        </div>

        {confirmDelete && (
          <div className="mx-5 mt-4 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/30">
            <p className="text-sm text-red-700 dark:text-red-300">
              Remove this transaction?
            </p>
            <div className="mt-2 flex gap-2">
              <Button
                size="sm"
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteTransaction.isPending}
              >
                {deleteTransaction.isPending ? "Removing..." : "Remove"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirmDelete(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="px-5 pb-8">
          {/* Amount banner */}
          <div className="mt-4 rounded-lg border bg-muted/30 p-4">
            <div className="grid grid-cols-2 gap-4">
              <EditableBannerField
                label="Net amount"
                value={entry.amountNet}
                field="amountNet"
                onSave={handleFieldSave}
                isViewer={isViewer}
              />
              {entry.bookValue != null && (
                <EditableBannerField
                  label="Book value"
                  value={entry.bookValue}
                  field="bookValue"
                  onSave={handleFieldSave}
                  isViewer={isViewer}
                />
              )}
            </div>
          </div>

          {/* Transaction details */}
          <SectionHeader>Transaction details</SectionHeader>
          <SelectField
            label="Direction"
            value={entry.direction}
            options={DIRECTION_LABELS}
            field="direction"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <SelectField
            label="Category"
            value={entry.transactionCategory}
            options={CATEGORY_LABELS}
            field="transactionCategory"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <SelectField
            label="Asset class"
            value={entry.assetClass}
            options={CLASS_LABELS}
            field="assetClass"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <EditableField
            label="Asset description"
            value={entry.assetDescription}
            field="assetDescription"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <EditableField
            label="Asset identifier"
            value={entry.assetIdentifier}
            field="assetIdentifier"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <EditableField
            label="Transaction date"
            value={entry.transactionDate}
            displayValue={fmtDate(entry.transactionDate)}
            field="transactionDate"
            type="date"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <EditableField
            label="Gross amount"
            value={entry.amountGross}
            displayValue={
              entry.amountGross != null
                ? fmtCurrency(entry.amountGross)
                : undefined
            }
            field="amountGross"
            type="number"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <EditableField
            label="GST"
            value={entry.amountGst}
            displayValue={
              entry.amountGst != null ? fmtCurrency(entry.amountGst) : undefined
            }
            field="amountGst"
            type="number"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <div className="mt-2 border-b" />

          {/* Status */}
          <SectionHeader>Status</SectionHeader>
          <SelectField
            label="Payment"
            value={entry.paymentStatus}
            options={PAYMENT_LABELS}
            field="paymentStatus"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <SelectField
            label="Agreement"
            value={entry.agreementStatus}
            options={AGREEMENT_LABELS}
            field="agreementStatus"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <SelectField
            label="Review"
            value={entry.reviewStatus}
            options={REVIEW_LABELS}
            field="reviewStatus"
            onSave={handleFieldSave}
            isViewer={isViewer}
          />
          <div className="mt-2 border-b" />

          {/* Notes */}
          <SectionHeader>Notes</SectionHeader>
          {isViewer ? (
            <p className="whitespace-pre-line py-2 text-sm leading-relaxed text-foreground">
              {entry.notes || "\u2014"}
            </p>
          ) : (
            <Textarea
              key={entry._id}
              defaultValue={entry.notes ?? ""}
              onBlur={(e) => {
                if (e.target.value !== (entry.notes ?? ""))
                  handleFieldSave("notes", e.target.value);
              }}
              placeholder="Add notes..."
              className="mt-2 min-h-[140px] resize-y p-3 text-sm leading-relaxed"
            />
          )}
          <div className="mt-4 border-b" />

          {/* Source documents */}
          {entry.sourceDocuments.length > 0 && (
            <>
              <SectionHeader>Source documents</SectionHeader>
              <div className="flex flex-col gap-2 pt-2.5">
                {entry.sourceDocuments.map((doc, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() =>
                      setViewingDocumentId(doc.documentId as Id<"documents">)
                    }
                    className="group flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors hover:bg-muted/50"
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                      <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-foreground">
                        {doc.fileName}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/30 transition-colors group-hover:text-muted-foreground" />
                  </button>
                ))}
              </div>
              <div className="mt-4 border-b" />
            </>
          )}

          {/* History */}
          {entry.changeHistory && (
            <div className="mt-4">
              <ChangeHistory
                history={entry.changeHistory}
                usersById={historyUsersById}
                formatters={ASSET_TRANSACTION_FIELD_FORMATTERS}
                fieldLabels={ASSET_TRANSACTION_FIELD_LABELS}
              />
            </div>
          )}
        </div>
      </div>
      <DocumentDetail
        documentId={viewingDocumentId}
        isViewer
        open={viewingDocumentId !== null}
        onClose={() => setViewingDocumentId(null)}
      />

      {/* Confirmation gate for every inline edit in this panel */}
      <ConfirmEditDialog
        edit={pendingEdit}
        onClose={() => setPendingEdit(null)}
      />
    </>
  );
}
